<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button 
        class="btn btn-primary" 
        @click="handleCreate"
        :disabled="!hasPermission('plan:create')"
      >
        <i class="ri-add-line"></i>
        新增方案
      </button>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋方案代碼或方案名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchPlans">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>方案代碼</th>
            <th>方案名稱</th>
            <th>方案類型</th>
            <th>月費</th>
            <th>年費</th>
            <th>試用天數</th>
            <th>員工帳號上限</th>
            <th>門市數上限</th>
            <th>會員數上限</th>
            <th>狀態</th>
            <th>預設方案</th>
            <th>開放自訂閱</th>
            <th>官網顯示</th>
            <th>使用客戶數</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="plans.length === 0">
            <td colspan="15" class="empty-state">
              沒有找到方案
            </td>
          </tr>
          <tr v-for="plan in plans" :key="plan.id">
            <td>{{ plan.planCode }}</td>
            <td>{{ plan.planName }}</td>
            <td>
              <span :class="getPlanTypeBadgeClass(plan.planType)">
                {{ getPlanTypeName(plan.planType) }}
              </span>
            </td>
            <td>{{ formatPrice(plan.monthlyPrice) }}</td>
            <td>{{ formatPrice(plan.yearlyPrice) }}</td>
            <td>{{ plan.planType === 'trial' ? (plan.trialDays || '-') + ' 天' : '-' }}</td>
            <td>{{ plan.maxStaffAccounts || '-' }}</td>
            <td>{{ plan.maxStores || '-' }}</td>
            <td>{{ plan.maxMembers || '-' }}</td>
            <td>
              <span
                :class="plan.status === 'active' ? 'badge badge-success' : 'badge badge-danger'"
              >
                {{ plan.status === 'active' ? '啟用' : '停用' }}
              </span>
            </td>
            <td>
              <span v-if="plan.isDefault" class="badge badge-primary">是</span>
              <span v-else>-</span>
            </td>
            <td>
              <label class="toggle-switch" :title="plan.isSelfSubscribable ? '已開放，點擊關閉' : '未開放，點擊開啟'">
                <input
                  type="checkbox"
                  :checked="plan.isSelfSubscribable"
                  :disabled="!hasPermission('plan:update') || togglingId === plan.id"
                  @change="toggleSelfSubscribable(plan)"
                />
                <span class="toggle-slider"></span>
              </label>
            </td>
            <td>
              <label class="toggle-switch" :title="plan.showOnWebsite !== false ? '官網顯示中，點擊關閉' : '官網不顯示，點擊開啟'">
                <input
                  type="checkbox"
                  :checked="plan.showOnWebsite !== false"
                  :disabled="!hasPermission('plan:update') || togglingWebsiteId === plan.id"
                  @change="toggleShowOnWebsite(plan)"
                />
                <span class="toggle-slider"></span>
              </label>
            </td>
            <td>{{ plan.clientCount || 0 }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(plan)"
                  title="編輯"
                  :disabled="!hasPermission('plan:update')"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(plan)"
                  :title="getDeleteButtonTitle(plan)"
                  :disabled="!hasPermission('plan:delete') || plan.isDefault || Number(plan.clientCount) > 0"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { usePermissions } from '@/composables/usePermissions.js';
import { planService } from '@/api/modules/plans.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'PlanList',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('方案列表');
    const { hasPermission } = usePermissions();
    const plans = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const togglingId = ref(null);
    const togglingWebsiteId = ref(null);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const searchTimeout = ref(null);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchPlans = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        // 只有當搜尋關鍵字不為空時才加入參數
        if (searchQuery.value && searchQuery.value.trim().length > 0) {
          params.search = searchQuery.value.trim();
        }

        const response = await planService.getPlans(params);

        if (response && response.success && response.data) {
          // 處理分頁響應格式
          if (response.data.items) {
            plans.value = response.data.items || [];
            total.value = response.data.pagination?.totalItems || response.data.pagination?.total || response.data.total || 0;
          } else if (Array.isArray(response.data)) {
            plans.value = response.data;
            total.value = response.data.length;
          }
        } else {
          error.value = response && response.message || '獲取方案列表失敗';
        }
      } catch (err) {
        console.error('獲取方案列表失敗:', err);
        error.value = err.message || '獲取方案列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchPlans();
      }, 500);
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        // 更新 URL query 參數
        router.push({
          path: route.path,
          query: { ...route.query, page: page }
        });
        fetchPlans();
      }
    };

    const handleCreate = () => {
      router.push('/system/plans/create');
    };

    const handleEdit = (plan) => {
      // 保存當前頁碼到 URL query，編輯完返回時可以恢復
      router.push({
        path: `/system/plans/${plan.id}/edit`,
        query: { returnPage: currentPage.value }
      });
    };

    const handleDelete = async (plan) => {
      // 檢查是否為預設方案
      if (plan.isDefault) {
        await showError('無法刪除', '預設方案無法刪除');
        return;
      }

      // 檢查是否有客戶正在使用
      const clientCount = Number(plan.clientCount) || 0;
      if (clientCount > 0) {
        await showError(
          '無法刪除',
          `方案「${plan.planName}」目前有 ${clientCount} 個客戶正在使用，無法刪除。`
        );
        return;
      }

      const confirmed = await showDeleteConfirm(`${plan.planName} (${plan.planCode})`);
      
      if (!confirmed) {
        return;
      }

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await planService.deletePlan(plan.id);

        if (response && response.success) {
          await showSuccess('刪除成功', `方案「${plan.planName}」已成功刪除`);
          
          if (plans.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          await fetchPlans();
        } else {
          const errorMsg = response?.message || '刪除方案失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除方案失敗:', err);
        let errorTitle = '刪除失敗';
        let errorText = '刪除方案失敗，請稍後再試';
        
        if (err.code === 'NOT_FOUND') {
          errorText = '方案不存在或已被刪除';
        } else if (err.code === 'FORBIDDEN') {
          errorText = '您沒有權限刪除此方案';
        } else if (err.response?.status === 404) {
          errorText = '方案不存在或路由錯誤';
        } else if (err.response?.status === 403) {
          errorText = '您沒有權限刪除此方案';
        } else if (err.message) {
          errorText = err.message;
        }
        
        await showError(errorTitle, errorText);
      } finally {
        loading.value = originalLoading;
      }
    };

    const getDeleteButtonTitle = (plan) => {
      if (plan.isDefault) {
        return '預設方案無法刪除';
      }
      const clientCount = Number(plan.clientCount) || 0;
      if (clientCount > 0) {
        return `此方案有 ${clientCount} 個客戶正在使用，無法刪除`;
      }
      return '刪除';
    };

    const toggleSelfSubscribable = async (plan) => {
      togglingId.value = plan.id;
      try {
        const updated = await planService.updatePlan(plan.id, {
          isSelfSubscribable: !plan.isSelfSubscribable
        });
        plan.isSelfSubscribable = updated.data?.isSelfSubscribable ?? !plan.isSelfSubscribable;
        await showSuccess(
          '已更新',
          plan.isSelfSubscribable ? `${plan.planName} 已開放客戶自訂閱` : `${plan.planName} 已關閉自訂閱`
        );
      } catch (err) {
        await showError('更新失敗', err.response?.data?.message || err.message);
      } finally {
        togglingId.value = null;
      }
    };

    const toggleShowOnWebsite = async (plan) => {
      const next = plan.showOnWebsite === false;
      togglingWebsiteId.value = plan.id;
      try {
        const updated = await planService.updatePlan(plan.id, {
          showOnWebsite: next
        });
        plan.showOnWebsite = updated.data?.showOnWebsite ?? next;
        await showSuccess(
          '已更新',
          plan.showOnWebsite !== false
            ? `${plan.planName} 已於官網顯示`
            : `${plan.planName} 已自官網隱藏`
        );
      } catch (err) {
        await showError('更新失敗', err.response?.data?.message || err.message);
      } finally {
        togglingWebsiteId.value = null;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    const formatPrice = (price) => {
      if (price === null || price === undefined) return '-';
      return `NT$ ${Number(price).toLocaleString('zh-TW')}`;
    };

    const LEGACY_FORMAL_TYPES = ['basic', 'professional', 'enterprise'];

    const getPlanTypeName = (planType) => {
      if (!planType) return '-';
      if (LEGACY_FORMAL_TYPES.includes(planType)) return '正式版';
      const typeMap = {
        trial: '試用版',
        formal: '正式版',
        custom: '客製版'
      };
      return typeMap[planType] || planType;
    };

    const getPlanTypeBadgeClass = (planType) => {
      if (LEGACY_FORMAL_TYPES.includes(planType)) {
        return 'badge badge-info';
      }
      const classMap = {
        trial: 'badge badge-warning',
        formal: 'badge badge-info',
        custom: 'badge badge-purple'
      };
      return classMap[planType] || 'badge badge-secondary';
    };

    onMounted(() => {
      // 從 URL query 恢復頁碼（編輯完返回時）
      const pageFromQuery = parseInt(route.query.page);
      if (pageFromQuery && pageFromQuery > 0) {
        currentPage.value = pageFromQuery;
      }
      fetchPlans();
    });

    // 監聽 route 變化，當從編輯頁返回時恢復頁碼
    watch(() => route.query.page, (newPage) => {
      if (newPage) {
        const page = parseInt(newPage);
        if (page > 0 && page !== currentPage.value) {
          currentPage.value = page;
          fetchPlans();
        }
      }
    });

    return {
      plans,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      total,
      pageTitle,
      hasPermission,
      handleSearch,
      goToPage,
      handleCreate,
      handleEdit,
      handleDelete,
      getDeleteButtonTitle,
      togglingId,
      togglingWebsiteId,
      toggleSelfSubscribable,
      toggleShowOnWebsite,
      formatDate,
      formatPrice,
      getPlanTypeName,
      getPlanTypeBadgeClass,
      fetchPlans,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

/* 頁面特定樣式覆寫 */
.table-wrapper table {
  min-width: 1280px; /* 方案列表欄位較多 */
}
/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  cursor: pointer;
  margin: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background: #ccc;
  border-radius: 22px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.toggle-switch input:checked + .toggle-slider {
  background: #1976d2;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(18px);
}

.toggle-switch input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
